import { Language } from '../../languages/languageHandler';
import { toMoment } from '../../utils/date';

export const formatDateTime = (dateTime?: Date | string | null): string => {
  const m = toMoment(dateTime ?? undefined);
  if (!m.isValid()) return '-';

  const hasTime = m.format('HH:mm:ss') !== '00:00:00';
  return hasTime ? m.format('YYYY-MM-DD HH:mm') : m.format('YYYY-MM-DD');
};

export const buildTicketUrl = (eid?: number, entid?: number, sn?: string) => {
  const searchParams = new URLSearchParams();

  if (eid) searchParams.set('eid', eid.toString());
  if (entid) searchParams.set('entid', entid.toString());
  if (sn) searchParams.set('sn', sn);

  searchParams.set('closed', '');

  return {
    pathname: '/helpdesk/tickets',
    search: `?${searchParams.toString()}`
  };
};

export const getUrgencyType = (lang: Language, urgency: number): string => {
  switch (urgency) {
    case 0:
      return lang.HELPDESK_TICKETS_URGENCY_0;
    case 1:
      return lang.HELPDESK_TICKETS_URGENCY_1;
    case 2:
      return lang.HELPDESK_TICKETS_URGENCY_2;
    case 3:
      return lang.HELPDESK_TICKETS_URGENCY_3;
    case 4:
      return lang.HELPDESK_TICKETS_URGENCY_4;
    default:
      throw new Error(`Invalid urgency ID: ${urgency}`);
  }
};

//TODO: create new type TicketStatus
export const getTicketStatus = (lang: Language, status: number): any => {
  switch (status) {
    case 0:
      return { status, name: lang.HELPDESK_TICKET_STATUS_0 };
    case 1:
      return { status, name: lang.HELPDESK_TICKET_STATUS_1 };
    case 2:
      return { status, name: lang.HELPDESK_TICKET_STATUS_2 };
    case 3:
      return { status, name: lang.HELPDESK_TICKET_STATUS_3 };
    case 4:
      return { status, name: lang.HELPDESK_TICKET_STATUS_4 };
    case 5:
      return { status, name: lang.HELPDESK_TICKET_STATUS_5 };
    case 6:
      return { status, name: lang.HELPDESK_TICKET_STATUS_6, disabled: true };
    default:
      throw new Error(`Invalid ticket status status: ${status}`);
  }
};

//TODO: create new type DetermineStatus
export const determineStatus = (status: number, lang: Language) => {
  switch (status) {
    case 0:
      return lang.HELPDESK_TICKET_STATUS_0;
    case 1:
      return lang.HELPDESK_TICKET_STATUS_1;
    case 2:
      return lang.HELPDESK_TICKET_STATUS_2;
    case 3:
      return lang.HELPDESK_TICKET_STATUS_3;
    case 4:
      return lang.HELPDESK_TICKET_STATUS_4;
    case 5:
      return lang.HELPDESK_TICKET_STATUS_5;
    case 6:
      return lang.HELPDESK_TICKET_STATUS_6;
    default:
      return lang.HELPDESK_TICKET_STATUS_0;
  }
};

//TODO: create new type ContractStatus
export const detectContractState = (agreeState: number | undefined, lang: Language) => {
  switch (agreeState) {
    case 0:
      return lang.AGREE_STATE_0;
    case 1:
      return lang.AGREE_STATE_1;
    case 2:
      return lang.AGREE_STATE_2;
    case 3:
      return lang.AGREE_STATE_3;
    case 4:
      return lang.AGREE_STATE_4;
    default:
      return '';
  }
};

export const formatSyncCommand = (command: number | undefined, lang: Language) => {
  switch (command) {
    case 0:
      return '-';
    case 1:
      return lang.RESTART;
    case 2:
      return lang.SHUTDOWN;
    case 3:
      return lang.UNLOCK_ESCROW;
    case 4:
      return lang.OUT_OF_SERVICE;
    case 5:
      return lang.IN_SERVICE;
    case 6:
      return lang.OPEN_ESCROW;
    case 7:
      return lang.CLOSE_ESCROW;
    case 8:
      return lang.VPN_CONNECT;
    case 9:
      return lang.VPN_DISCONNECT;
    default:
      // for anything else not defined above, just show the number
      return command?.toString() ?? '';
  }
};

export const getCommunicationTypes = (lang: Language) => [
  { value: '', label: lang.ENTITY_TYPE_NULL },
  { value: '0', label: lang.USER_MOBILE },
  { value: '1', label: lang.USER_EMAIL },
  { value: '2', label: 'auto' },
  { value: '3', label: 'site' }
];

export const getIssueTypes = (lang: Language) => [
  { value: '', label: lang.ENTITY_TYPE_NULL },
  { value: '0', label: lang.ERROR },
  { value: '1', label: lang.HELPDESK_TICKET_UPDATE_CHANGE_REQUEST },
  { value: '2', label: lang.HELPDESK_TICKET_UPDATE_INFORMATION_REQUEST },
  { value: '3', label: lang.GLOBAL_OTHER }
];

export const getIntTypes = (lang: Language) => [
  { value: '', label: 'n/d' },
  { value: '0', label: lang.HELPDESK_TICKET_UPDATE_INT_TYPE_REMOTE },
  { value: '1', label: lang.HELPDESK_TICKET_UPDATE_INT_TYPE_LOCAL }
];

export const ticketStatus = (lang: Language) => [
  { value: '0', label: lang.HELPDESK_TICKET_STATUS_0 },
  { value: '1', label: lang.HELPDESK_TICKET_STATUS_1 },
  { value: '2', label: lang.HELPDESK_TICKET_STATUS_2 },
  { value: '3', label: lang.HELPDESK_TICKET_STATUS_3 },
  { value: '4', label: lang.HELPDESK_TICKET_STATUS_4 },
  { value: '5', label: lang.HELPDESK_TICKET_STATUS_5 },
  { value: '6', label: lang.HELPDESK_TICKET_STATUS_6 }
];

export const ticketSolution = (lang: Language) => [
  { value: 'true', label: lang.YES },
  { value: 'false', label: lang.NO }
];

export const ticketImpact = (lang: Language) => [
  { value: '0', label: lang.GLOBAL_GENERAL },
  { value: '1', label: lang.GLOBAL_PARTICULAR }
];

export const ticketUrgency = (lang: Language) => [
  { value: '0', label: lang.HELPDESK_TICKETS_URGENCY_0 },
  { value: '1', label: lang.HELPDESK_TICKETS_URGENCY_1 },
  { value: '2', label: lang.HELPDESK_TICKETS_URGENCY_2 },
  { value: '3', label: lang.HELPDESK_TICKETS_URGENCY_3 },
  { value: '4', label: lang.HELPDESK_TICKETS_URGENCY_4 }
];

export const ticketClassification = (lang: Language) => [
  { value: '0', label: lang.HELPDESK_TICKET_CLASSIFICATION_0 },
  { value: '1', label: lang.HELPDESK_TICKET_CLASSIFICATION_1 },
  { value: '2', label: lang.HELPDESK_TICKET_CLASSIFICATION_2 },
  { value: '3', label: lang.HELPDESK_TICKET_CLASSIFICATION_3 }
];

export const entityTypes = (lang: Language) => [
  { value: '', label: lang.ENTITY_TYPE_NULL },
  { value: '0', label: lang.ENTITY_TYPE_0 },
  { value: '1', label: lang.ENTITY_TYPE_1 },
  { value: '2', label: lang.ENTITY_TYPE_2 },
  { value: '3', label: lang.ENTITY_TYPE_3 }
];

export const revType = (lang: Language) => [
  { value: '0', label: lang.HELPDESK_CONTRACT_REV_TYPE_1 },
  { value: '1', label: lang.HELPDESK_CONTRACT_REV_TYPE_2 },
  { value: '2', label: lang.HELPDESK_CONTRACT_REV_TYPE_3 }
];

export const invoiceState = (lang: Language) => [
  { value: '0', label: lang.HELPDESK_CONTRACT_INVOICE_STATE_1 },
  { value: '1', label: lang.HELPDESK_CONTRACT_INVOICE_STATE_2 },
  { value: '2', label: lang.HELPDESK_CONTRACT_INVOICE_STATE_3 }
];

export const billingPeriod = (lang: Language) => [
  { value: '0', label: lang.HELPDESK_CONTRACT_BILLING_PERIOD_1 },
  { value: '1', label: lang.HELPDESK_CONTRACT_BILLING_PERIOD_2 },
  { value: '2', label: lang.HELPDESK_CONTRACT_BILLING_PERIOD_3 },
  { value: '3', label: lang.HELPDESK_CONTRACT_BILLING_PERIOD_4 },
  { value: '4', label: lang.HELPDESK_CONTRACT_BILLING_PERIOD_5 }
];

export const contractState = (lang: Language) => [
  { value: '0', label: lang.HELPDESK_CONTRACT_STATE_1 },
  { value: '1', label: lang.HELPDESK_CONTRACT_STATE_2 },
  { value: '2', label: lang.HELPDESK_CONTRACT_STATE_3 },
  { value: '3', label: lang.HELPDESK_CONTRACT_STATE_4 },
  { value: '4', label: lang.HELPDESK_CONTRACT_STATE_5 }
];

export const getAuditTypeDescription = (auditType: number, lang: Language) => {
  switch (auditType) {
    case 1:
      return lang.CREDIT_CARD_INSERTED;
    case 2:
      return lang.NON_READABLE_CARD;
    case 3:
      return lang.READABLE_CARD;
    case 4:
      return lang.GENERATED_NEW_TRANSACTION_KEY;
    case 5:
      return lang.CHECK_OPERATION_REQUESTED;
    case 6:
      return lang.CLIENT_CARD_RETURNED;
    case 7:
      return lang.CLIENT_CARD_REMOVED;
    case 8:
      return lang.CHECK_OPERATION_GOOD_RESPONSE;
    case 9:
      return lang.CHECK_OPERATION_ERROR;
    case 10:
      return lang.CLIENT_ACCOUNT_SELECTED;
    case 11:
      return lang.CLIENT_CARD_INSERTED;
    case 12:
      return lang.CHECK_EXTERNAL_ACCOUNT_OPERATION_REQUESTED;
    case 13:
      return lang.CHECK_EXTERNAL_ACCOUNT_OPERATION_GOOD_RESPONSE;
    case 14:
      return lang.CHECK_EXTERNAL_ACCOUNT_OPERATION_ERROR;
    case 15:
      return lang.CLIENT_CANCEL;
    case 16:
      return lang.CLIENT_CHOOSE_OR_INSERT_DEPOSIT_REASON;
    case 17:
      return lang.ENABLED_BANKNOTES_VALIDATOR;
    case 18:
      return lang.CLIENT_ACCEPTS_BANKNOTES_COUNT;
    case 19:
      return lang.CLIENT_REJECT_BAKNOTES_COUNT;
    case 20:
      return lang.DECLARATION_AMOUNT_REACHED;
    case 21:
      return lang.DECLARATION_SELECTED;
    case 22:
      return lang.DEPOSIT_OPERATION_REQUESTED;
    case 23:
      return lang.DEPOSIT_OPERATION_GOOD_RESPONSE;
    case 24:
      return lang.DEPOSIT_OPERATION_ERROR;
    case 25:
      return lang.BANKNOTES_SENT_TO_SAFE;
    case 26:
      return lang.BANKNOTES_DELIVERED_TO_CLIENT;
    case 27:
      return lang.RECEIPT_PRINTED;
    case 28:
      return lang.GOOD_USER_IDENTIFICATION;
    case 29:
      return lang.BAD_USER_IDENTIFICATION;
    case 30:
      return lang.PERIOD_CLOSE_OPERATION;
    case 31:
      return lang.PERIOD_DATA_SAVED;
    case 32:
      return lang.SAFE_DOOR_OPENED;
    case 33:
      return lang.BAG_REMOVED;
    case 34:
      return lang.BAG_INSERTED;
    case 35:
      return lang.SAFE_DOOR_CLOSED;
    case 36:
      return lang.NEW_PERIOD_OPEN;
    case 37:
      return lang.CASH_BALANCE;
    case 38:
      return lang.EJECT_CARD;
    case 39:
      return lang.LAST_PERIOD_REPORT;
    case 40:
      return lang.SHUTDOWN;
    case 41:
      return lang.RESTART;
    case 42:
      return lang.CLIENT_CARD_NOT_REMOVED;
    case 43:
      return lang.MINIMUM_AMOUNT_REACHED;
    case 44:
      return lang.MAXIMUM_AMOUNT_REACHED;
    case 45:
      return lang.SHUTDOWN_BY_UPS_ON_BATTERY;
    case 46:
      return lang.REMOTE_SHUTDOWN;
    case 47:
      return lang.REMOTE_RESTART;
    case 48:
      return lang.REMOTE_UNLOCK_ESCROW;
    case 49:
      return lang.LOCAL_UNLOCK_ESCROW;
    case 50:
      return lang.MANUAL_OUT_OF_SERVICE_REMOVED;
    case 51:
      return lang.CENTRAL_OUT_OF_SERVICE;
    case 52:
      return lang.LOCAL_CLOSE_ESCROW;
    case 53:
      return lang.LOCAL_OPEN_ESCROW;
    case 54:
      return lang.REMOTE_CLOSE_ESCROW;
    case 55:
      return lang.REMOTE_OPEN_ESCROW;
    case 56:
      return lang.INVALID_ESCROW_MOVEMENT;
    case 57:
      return lang.ESCROW_NOT_EMPTY_DETECTED;
    case 58:
      return lang.INVALID_ACCOUNTS;
    case 59:
      return lang.EXISTENT_CARD;
    case 60:
      return lang.WRONG_PIN;
    case 61:
      return lang.WRONG_PIN_3RD_ATTEMPT;
    case 63:
      return lang.VALID_PIN;
    case 64:
      return lang.NONEXISTENT_CARD;
    case 65:
      return lang.BANKNOTES_VALIDATOR_ERROR;
    case 66:
      return lang.FAKE_NOTES_DETECTED;
    case 67:
      return lang.FAKE_NOTES_DETECTED_CLEARED;
    case 68:
      return lang.NONEXISTENT_USER;
    case 69:
      return lang.EXISTENT_USER;
    case 70:
      return lang.REJECT_BLOCKER_ENABLE;
    case 71:
      return lang.REJECT_BLOCKER_DISABLE;
    case 72:
      return lang.FAKE_NOTES_DETECTED_CLEARED_LOCALLY;
    case 1000:
      return lang.ATYPE_USER_INSERTED;
    case 1001:
      return lang.ATYPE_USER_DELETED;
    case 1002:
      return lang.ATYPE_USER_UPDATED;
    case 1003:
      return lang.ATYPE_EQUIPMENT_INSERTED;
    case 1004:
      return lang.ATYPE_EQUIPMENT_DELETED;
    case 1005:
      return lang.ATYPE_EQUIPMENT_UPDATED;
    case 1006:
      return lang.ATYPE_OPERATION_RESTART_REQUESTED;
    case 1007:
      return lang.ATYPE_OPERATION_RESTART_SENT_TO_EQUIPMENT;
    case 1008:
      return lang.ATYPE_OPERATION_RESTART_EXPIRED;
    case 1009:
      return lang.ATYPE_OPERATION_SHUTDOWN_REQUESTED;
    case 1010:
      return lang.ATYPE_OPERATION_SHUTDOWN_SENT_TO_EQUIPMENT;
    case 1011:
      return lang.ATYPE_OPERATION_SHUTDOWN_EXPIRED;
    case 1012:
      return lang.ATYPE_OPERATION_UNLOCK_ESCROW_REQUESTED;
    case 1013:
      return lang.ATYPE_OPERATION_UNLOCK_ESCROW_SENT_TO_EQUIPMENT;
    case 1014:
      return lang.ATYPE_OPERATION_UNLOCK_ESCROW_EXPIRED;
    case 1015:
      return lang.ATYPE_OPERATION_OUT_OF_SERVICE_REQUESTED;
    case 1016:
      return lang.ATYPE_OPERATION_OUT_OF_SERVICE_SENT_TO_EQUIPMENT;
    case 1017:
      return lang.ATYPE_OPERATION_OUT_OF_SERVICE_EXPIRED;
    case 1018:
      return lang.ATYPE_OPERATION_IN_SERVICE_REQUESTED;
    case 1019:
      return lang.ATYPE_OPERATION_IN_SERVICE_SENT_TO_EQUIPMENT;
    case 1020:
      return lang.ATYPE_OPERATION_IN_SERVICE_EXPIRED;
    case 1021:
      return lang.ATYPE_OPERATION_OPEN_ESCROW_REQUESTED;
    case 1022:
      return lang.ATYPE_OPERATION_OPEN_ESCROW_SENT_TO_EQUIPMENT;
    case 1023:
      return lang.ATYPE_OPERATION_OPEN_ESCROW_EXPIRED;
    case 1024:
      return lang.ATYPE_OPERATION_CLOSE_ESCROW_REQUESTED;
    case 1025:
      return lang.ATYPE_OPERATION_CLOSE_ESCROW_SENT_TO_EQUIPMENT;
    case 1026:
      return lang.ATYPE_OPERATION_CLOSE_ESCROW_EXPIRED;
    case 1027:
      return lang.ATYPE_ENTITY_INSERTED;
    case 1028:
      return lang.ATYPE_ENTITY_DELETED;
    case 1029:
      return lang.ATYPE_ENTITY_UPDATED;
    case 1030:
      return lang.ATYPE_LOCATION_INSERTED;
    case 1031:
      return lang.ATYPE_LOCATION_DELETED;
    case 1032:
      return lang.ATYPE_LOCATION_UPDATED;
    case 1033:
      return lang.ATYPE_CURRENCY_INSERTED;
    case 1034:
      return lang.ATYPE_CURRENCY_DELETED;
    case 1035:
      return lang.ATYPE_CURRENCY_UPDATED;
    case 1036:
      return lang.ATYPE_OPERATION_IGNORED;
    case 1037:
      return lang.ATYPE_PRODUCT_EDITED;
    case 1038:
      return lang.ATYPE_PRODUCT_DISPENSER_EDITED;
    case 1039:
      return lang.ATYPE_UPDATE_USER_BALANCE;
    case 1040:
      return lang.ATYPE_OPERATION_VPN_CONNECT;
    case 1041:
      return lang.ATYPE_OPERATION_VPN_DISCONNECT;
    case 1042:
      return lang.ATYPE_OPERATION_VPN_EXPIRED;
    case 1043:
      return lang.ATYPE_OPERATION_VPN_SENT_TO_EQUIPMENT;
    default:
      return lang.ATYPE_ERROR;
  }
};
